import useMouseClickOutside from "hooks/MouseClickOutside";
import { useRef, useState } from "react";
import { MdMoreHoriz } from "react-icons/md";
import { formatLastAccessDate } from "utils/formatDate";
import { ContactListType } from "utils/interfaces";
import EditDeleteMenu from "../components/EditDeleteMenu";

interface StaffCardProps {
  staff: ContactListType;
  handleCardClick: (event: React.MouseEvent) => void;
  handleDelete: (event: React.MouseEvent) => void;
}

const StaffCard: React.FC<StaffCardProps> = ({
  staff,
  handleCardClick,
  handleDelete,
}) => {
  // States definition
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);

  // Create a menu ref
  const menuRef: React.RefObject<HTMLDivElement> = useRef(null);

  // Define the last access date
  const lastAccess =
    staff && staff.lastActiveAt
      ? formatLastAccessDate(staff.lastActiveAt)
      : null;

  // Define a parameter for the role
  const staffRole = staff.role ? staff.role : "";

  // Define the role text based on the user role
  const roleText = staffRole
    ? staffRole === "class admin"
      ? "staff admin" // Just transform the class admin role into "staff admin"
      : staffRole
    : "-";

  // Define the color of the role text based on the role
  const roleColor =
    staffRole === "master admin"
      ? { bg: "bg-[#FAD6F7]", text: "text-[#5B0B53]" }
      : staffRole === "customer admin"
      ? { bg: "bg-[#D6F7FA]", text: "text-[#0B535B]" }
      : staffRole === "class admin"
      ? { bg: "bg-primary-200", text: "text-[#553005]" }
      : { bg: "bg-neutral-100", text: "text-neutral-500" };

  /**
   * Hook used when clicking outside the dropdown menu
   */
  useMouseClickOutside(
    menuRef,
    () => setShowOptionsMenu(false),
    showOptionsMenu
  );

  // Define the activateEditButton value
  const includeEdit = staff.canEdit || false;
  const includeDelete = staff.canDelete || false;
  const activateEditButton = (includeEdit || includeDelete);

  /**
   * Define a deactivated function in order to prevent to execute the
   * function that underlies a button
   * @param event
   */
  const deactivatedFunction = (event: React.MouseEvent) => {
    // Prevent clicking the card
    event.stopPropagation();
  };

  /**
   * Function used to open the pop up menu with the edit and
   * delete option
   * @param event
   */
  const handleClickOptions = (event: React.MouseEvent) => {
    // First of all, prevent clicking the card
    event.stopPropagation();

    // Toggle the state
    setShowOptionsMenu(!showOptionsMenu);
  };

  /**
   * Function used to wrap the edit or delete functions
   * in order to add custom functionalities per card
   */
  const handleClickOptionWrapper = (
    func: (event: React.MouseEvent) => void
  ) => {
    return (event: React.MouseEvent) => {
      // Close the menu when clicking on one of the options
      setShowOptionsMenu(false);

      // And execute the function
      func(event);
    };
  };

  return (
    <div className="w-full h-20 px-6 py-4 bg-neutral-25 rounded-xl justify-between items-center inline-flex">
      <div className="w-[190px] self-stretch flex-col justify-center items-start inline-flex gap-1.5">
        <div className="self-stretch text-neutral-700 text-lg font-semibold font-sans leading-snug overflow-hidden break-keep whitespace-nowrap text-ellipsis">
          {staff.firstName && staff.lastName
            ? `${staff.firstName} ${staff.lastName}`
            : "-"}
        </div>
        <div className="self-stretch justify-start text-neutral-500 text-sm font-normal font-sans leading-[17px] overflow-hidden break-keep whitespace-nowrap text-ellipsis">
          {staff.email || "-"}
        </div>
      </div>
      <div className="w-28 inline-flex flex-col justify-center items-center gap-1">
        <div
          className={`px-1.5 py-0.5 ${roleColor.bg} rounded flex justify-center items-center gap-2`}
        >
          <div
            className={`text-center justify-start ${roleColor.text} text-[10px] font-normal font-sans uppercase leading-3 tracking-wide select-none`}
          >
            {roleText}
          </div>
        </div>
        <div className="text-center justify-start text-gray-500 text-[10px] font-normal font-sans uppercase leading-3 tracking-wide">
          Roles
        </div>
      </div>
      <div className="w-[150px] inline-flex flex-col justify-center items-center gap-1">
        {lastAccess ? (
          <div className="text-center justify-start text-neutral-500 text-sm font-normal font-sans leading-none">
            {lastAccess}
          </div>
        ) : (
          <div className="px-1.5 py-0.5 bg-secondary-600 opacity-70 rounded flex justify-center items-center gap-2">
            <div className="text-center justify-start text-white text-[10px] font-semibold font-sans uppercase leading-3 tracking-wide select-none">
              Invite sent
            </div>
          </div>
        )}
        <div className="text-center justify-start text-neutral-500 text-[10px] font-normal font-sans uppercase leading-3 tracking-wide">
          Last accessed
        </div>
      </div>
      <div
        className="self-stretch justify-center items-center gap-3 inline-flex relative"
        ref={menuRef}
      >
        <button
          id={`more-options-${staff._id}`}
          aria-label="more-options"
          className={`p-3 bg-neutral-100 rounded-full justify-start items-start gap-2 flex z-20 ${
            activateEditButton
              ? "hover:bg-neutral-200 ease-in-out duration-300 cursor-pointer"
              : "cursor-not-allowed"
          }`}
          onClick={
            activateEditButton ? handleClickOptions : deactivatedFunction
          }
          title={
            activateEditButton
              ? "More options"
              : "Not allowed to edit this user"
          }
        >
          <MdMoreHoriz
            className={`w-6 h-6 ${
              activateEditButton ? "fill-neutral-700" : "fill-neutral-200"
            }`}
          />
        </button>
        <EditDeleteMenu
          show={showOptionsMenu}
          includeEdit={includeEdit}
          includeDelete={includeDelete}
          handleEdit={handleClickOptionWrapper(handleCardClick)}
          handleDelete={handleClickOptionWrapper(handleDelete)}
          containerStyle="absolute w-fit top-[54px] right-0 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex z-30"
        />
      </div>
    </div>
  );
};

export default StaffCard;
